import type { MentionSuggestion } from 'common/api/endpoints/mentions';
import type { StrippedUser } from 'common/api/endpoints/users';
import type { MimeTypeValues } from 'common/constants/files';
import type {
  DefaultFieldNames,
  DefaultFieldTypeMap,
  IdeaFieldTypes,
} from 'common/constants/ideas';

export type IdeaCommentFile = {
  mimeType: MimeTypeValues | null;
  name: string;
  url: string;
};

export type IdeaComment = {
  _id: string;
  author: StrippedUser;
  companyID: string;
  created: string;
  deleted: boolean;
  deletedAt: string | null;
  files: IdeaCommentFile[];
  fileURLs: string[];
  ideaDeleted: boolean;
  ideaID: string;
  imageURLs: string[];
  mentionedUsers: MentionSuggestion[];
  parentID: string | null;
  value: string;
};

export type CreateIdeaCommentRequest<ID = string> = {
  fileURLs?: string;
  ideaID: ID;
  imageURLs?: string;
  parentID?: ID | null;
  value: string;
};

export type UpdateIdeaCommentRequest<ID = string> = {
  commentID: ID;
  fileURLs?: string;
  imageURLs?: string;
  value: string;
};

export type DeleteIdeaCommentRequest<ID = string> = {
  commentID: ID;
};

export type CommentResponse = {
  comment: IdeaComment;
};

export enum IdeaActivityType {
  comment = 'comment',
  eventLog = 'eventLog',
  mergedIdea = 'mergedIdea',
}

type IdeaEventLogValueByType = {
  [IdeaFieldTypes.user]: { name: string; avatarURL?: string | null };
  [IdeaFieldTypes.text]: string | null;
  [IdeaFieldTypes.multilineText]: string | null;
  [IdeaFieldTypes.multiselect]: string[];
  [IdeaFieldTypes.date]: string | null;
  [IdeaFieldTypes.boolean]: boolean | null;
  [IdeaFieldTypes.number]: number | null;
  [IdeaFieldTypes.rating]: number | null;
  [IdeaFieldTypes.fibonacci]: number | null;
  [IdeaFieldTypes.range]: number | null;
  [IdeaFieldTypes.dropdown]: string | null;
  [IdeaFieldTypes.url]: string | null;
  [IdeaFieldTypes.formula]: number | null;
  [IdeaFieldTypes.score]: number | null;
};

type DefaultFieldValueMap = {
  [DefaultFieldNames.Status]: { name: string; color: string } | null;
  [DefaultFieldNames.Group]: string | null;
  [DefaultFieldNames.Themes]: string[];
};

type UnknownFieldValue = string | number | boolean | null;

export enum IdeaEventLogType {
  fieldChange = 'fieldChange',
  merge = 'merge',
  createSubIdea = 'createSubIdea',
}

type BaseIdeaEventLog = {
  _id: string;
  changer: StrippedUser | null;
  created: string;
};

export type FieldChangePayloadVariants =
  | {
      fieldName: DefaultFieldNames.Status;
      fieldType: DefaultFieldTypeMap[DefaultFieldNames.Status];
      newValue: DefaultFieldValueMap['status'];
      oldValue: DefaultFieldValueMap['status'];
    }
  | {
      fieldName: DefaultFieldNames.Group;
      fieldType: DefaultFieldTypeMap[DefaultFieldNames.Group];
      newValue: DefaultFieldValueMap['group'];
      oldValue: DefaultFieldValueMap['group'];
    }
  | {
      fieldName: DefaultFieldNames.Themes;
      fieldType: DefaultFieldTypeMap[DefaultFieldNames.Themes];
      newValue: DefaultFieldValueMap['themes'];
      oldValue: DefaultFieldValueMap['themes'];
    }
  | {
      fieldName?: string;
      fieldType: IdeaFieldTypes.user;
      newValue: IdeaEventLogValueByType[IdeaFieldTypes.user] | null;
      oldValue: IdeaEventLogValueByType[IdeaFieldTypes.user] | null;
    }
  | {
      fieldName?: string;
      fieldType: IdeaFieldTypes.text;
      newValue: IdeaEventLogValueByType[IdeaFieldTypes.text];
      oldValue: IdeaEventLogValueByType[IdeaFieldTypes.text];
    }
  | {
      fieldName?: string;
      fieldType: IdeaFieldTypes.multilineText;
      newValue: IdeaEventLogValueByType[IdeaFieldTypes.multilineText];
      oldValue: IdeaEventLogValueByType[IdeaFieldTypes.multilineText];
    }
  | {
      fieldName?: string;
      fieldType: IdeaFieldTypes.multiselect;
      newValue: IdeaEventLogValueByType[IdeaFieldTypes.multiselect];
      oldValue: IdeaEventLogValueByType[IdeaFieldTypes.multiselect];
    }
  | {
      fieldName?: string;
      fieldType: IdeaFieldTypes.date;
      newValue: IdeaEventLogValueByType[IdeaFieldTypes.date];
      oldValue: IdeaEventLogValueByType[IdeaFieldTypes.date];
    }
  | {
      fieldName?: string;
      fieldType: IdeaFieldTypes.boolean;
      newValue: IdeaEventLogValueByType[IdeaFieldTypes.boolean];
      oldValue: IdeaEventLogValueByType[IdeaFieldTypes.boolean];
    }
  | {
      fieldName?: string;
      fieldType: IdeaFieldTypes.number;
      newValue: IdeaEventLogValueByType[IdeaFieldTypes.number];
      oldValue: IdeaEventLogValueByType[IdeaFieldTypes.number];
    }
  | {
      fieldName?: string;
      fieldType: IdeaFieldTypes.rating;
      newValue: IdeaEventLogValueByType[IdeaFieldTypes.rating];
      oldValue: IdeaEventLogValueByType[IdeaFieldTypes.rating];
    }
  | {
      fieldName?: string;
      fieldType: IdeaFieldTypes.fibonacci;
      newValue: IdeaEventLogValueByType[IdeaFieldTypes.fibonacci];
      oldValue: IdeaEventLogValueByType[IdeaFieldTypes.fibonacci];
    }
  | {
      fieldName?: string;
      fieldType: IdeaFieldTypes.range;
      newValue: IdeaEventLogValueByType[IdeaFieldTypes.range];
      oldValue: IdeaEventLogValueByType[IdeaFieldTypes.range];
    }
  | {
      fieldName?: string;
      fieldType: IdeaFieldTypes.dropdown;
      newValue: IdeaEventLogValueByType[IdeaFieldTypes.dropdown];
      oldValue: IdeaEventLogValueByType[IdeaFieldTypes.dropdown];
    }
  | {
      fieldName?: string;
      fieldType: IdeaFieldTypes.url;
      newValue: IdeaEventLogValueByType[IdeaFieldTypes.url];
      oldValue: IdeaEventLogValueByType[IdeaFieldTypes.url];
    }
  | {
      fieldName?: string;
      fieldType: IdeaFieldTypes.formula;
      newValue: IdeaEventLogValueByType[IdeaFieldTypes.formula];
      oldValue: IdeaEventLogValueByType[IdeaFieldTypes.formula];
    }
  | {
      fieldName?: string;
      fieldType: IdeaFieldTypes.score;
      newValue: IdeaEventLogValueByType[IdeaFieldTypes.score];
      oldValue: IdeaEventLogValueByType[IdeaFieldTypes.score];
    }
  | {
      fieldName?: string;
      fieldType: null;
      newValue: UnknownFieldValue;
      oldValue: UnknownFieldValue;
    };

export type FieldChangeEventLog = BaseIdeaEventLog & {
  eventType: IdeaEventLogType.fieldChange;
  payload: FieldChangePayloadVariants;
};

export type MergeEventLogPayload<ID = string> = {
  mergedFromIdeaID: ID;
  mergedFromIdeaTitle: string;
};

export type MergeEventLog = BaseIdeaEventLog & {
  eventType: IdeaEventLogType.merge;
  payload: MergeEventLogPayload;
};

export type CreateSubIdeaEventLogPayload = {
  subIdeaTitle: string;
};

export type CreateSubIdeaEventLog = BaseIdeaEventLog & {
  eventType: IdeaEventLogType.createSubIdea;
  payload: CreateSubIdeaEventLogPayload;
};

export type IdeaEventLog = FieldChangeEventLog | MergeEventLog | CreateSubIdeaEventLog;

export type IdeaEventLogValue<T extends IdeaFieldTypes | null = null> = T extends IdeaFieldTypes
  ? T extends keyof IdeaEventLogValueByType
    ? IdeaEventLogValueByType[T] | null
    : UnknownFieldValue
  : UnknownFieldValue;

export type IdeaActivity =
  | {
      _id: string;
      created: string;
      type: IdeaActivityType.comment;
      comment: IdeaComment;
    }
  | {
      _id: string;
      created: string;
      eventLog: IdeaEventLog;
      type: IdeaActivityType.eventLog;
    };

export type GetIdeaActivityRequest<ID = string> = {
  ideaID: ID;
  cursor?: string | null;
  limit?: number;
  sort?: 'newest' | 'oldest';
};

export type GetIdeaActivityResponse = {
  items: IdeaActivity[];
  cursor: string | null;
  hasNextPage: boolean;
};
